// Logic for the Home (per-learner landing) view — extracted out of
// HomeView.tsx so the component only holds presentation. See
// specs/home-page-july6 (HomeC3) for the design.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useGlobalState } from '@/stores/globalState'
import { fetchAllGuides } from '@/lib/api/guides'
import { useGameTemplates } from '@/hooks/useGameTemplates'
import { getStageDisplay } from '@/data/stageDefinitions'
import { toHomeSets, type HomeSet } from '@/modules/home/homeSets'

const AVATAR_INKS = ['var(--blue)', 'var(--green)', 'var(--coral)', 'var(--band-reach)']
function avatarInk(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffff
  return AVATAR_INKS[h % AVATAR_INKS.length]
}

export function useHomeView() {
  const activeLearner = useGlobalState((s) => s.activeLearner)
  const activeLearnerId = useGlobalState((s) => s.activeLearnerId)
  const setActiveModule = useGlobalState((s) => s.setActiveModule)
  const setActiveWorkspaceItem = useGlobalState((s) => s.setActiveWorkspaceItem)
  const setWorkspaceView = useGlobalState((s) => s.setWorkspaceView)
  const openUpload = useGlobalState((s) => s.openUpload)
  const requestPrintAll = useGlobalState((s) => s.requestPrintAll)

  const { data: guides = [], isLoading } = useQuery({
    queryKey: ['reading-guides', activeLearnerId],
    queryFn: () => fetchAllGuides({ learnerId: activeLearnerId ?? undefined }),
    enabled: Boolean(activeLearnerId),
  })
  const { data: templates } = useGameTemplates()

  const learnerStage = activeLearner?.readingStage ?? null
  const sets = useMemo(
    () => toHomeSets(guides, learnerStage, templates),
    [guides, learnerStage, templates],
  )

  const [expanded, setExpanded] = useState(false)

  // ── actions ──────────────────────────────────────────────────────────────
  const openSet = (set: HomeSet) => {
    setActiveModule('MY_GUIDES')
    setActiveWorkspaceItem(set.id)
    setWorkspaceView(set.generationStatus === 'GENERATING' ? 'GENERATING' : 'CANVAS')
  }
  const printSet = (set: HomeSet) => {
    openSet(set)
    if (set.generationStatus !== 'GENERATING') {
      requestPrintAll(set.id)
    }
  }
  const browseBooks = () => {
    setActiveModule('BOOKS')
    setWorkspaceView('GALLERY')
  }
  const uploadPages = () => openUpload('home')
  const viewProfile = () => {
    setActiveModule('EVALUATION')
    setWorkspaceView('GALLERY')
  }

  if (!activeLearner) {
    return { activeLearner: null }
  }

  const name = activeLearner.name
  const initial = name.charAt(0).toUpperCase()
  const ink = avatarInk(activeLearner.id)
  const stageName = activeLearner.stageLabel ? getStageDisplay(activeLearner.stageLabel)?.name ?? null : null

  const empty = sets.length === 0
  const hero = sets[0]
  const rest = sets.slice(1)
  const shownRest = expanded ? rest : rest.slice(0, 4)
  // Sound Explorer (stage 0): no book games exist yet — swap the empty
  // state's browse/upload CTAs for the waitlist hero (DEP-179).
  const isSoundExplorer = activeLearner.readingStage === 0

  return {
    activeLearner,
    name,
    initial,
    ink,
    stageName,
    guides,
    isLoading,
    sets,
    empty,
    hero,
    rest,
    shownRest,
    expanded,
    setExpanded,
    isSoundExplorer,
    openSet,
    printSet,
    browseBooks,
    uploadPages,
    viewProfile,
  }
}
